/**
 * @typedef {Object} EmailSignatureState
 * @property {string|null} name - User's full name
 * @property {string|null} title - Job title
 * @property {string|null} email - Email address
 * @property {string|null} cell - Cell phone number
 * @property {string|null} desk - Desk phone number
 * @property {boolean} code - Whether to show code in signature
 * @property {string|null} banner - Selected banner option
 * @property {string|null} bannerAssetId - Asset ID of selected banner
 * @property {string|null} bannerTagline - Tagline text for banner
 * @property {number|null} assetWidth - Width of banner asset
 * @property {number} ruleHeight - Height of rule element
 * @property {string} url - Generated preview URL
 */

// Alpine.js component for managing email signature generation and persistence
/** @returns {EmailSignatureState & AlpineComponent} */
export default () => ({
    // Form field values for the signature
    name: null,
    title: null,
    email: null,
    cell: null,
    desk: null,
    code: true,
    banner: null,
    bannerAssetId: null,
    bannerTagline: null,
    assetWidth: null,
    ruleHeight: 0,
    url: '',
    
    /** @this {EmailSignatureState & AlpineComponent} */
    init() {
        // Load saved signature data and set up watchers for auto-saving
        this.loadFromStorage();
        this.$nextTick(() => {
            this.generateUrl();
        });
        // Watch for changes to update preview URL and save to localStorage
        this.$watch('name', () => this.updateAndSave());
        this.$watch('title', () => this.updateAndSave());
        this.$watch('email', () => this.updateAndSave());
        this.$watch('cell', () => this.updateAndSave());
        this.$watch('desk', () => this.updateAndSave());
        this.$watch('banner', () => this.updateAndSave());
    },

    /** 
     * Update preview URL and save changes to localStorage
     * @this {EmailSignatureState}
     */
    updateAndSave() {
        this.generateUrl();
        this.saveToStorage();
    },

    /** 
     * Load previously saved signature data from localStorage
     * @this {EmailSignatureState}
     */
    loadFromStorage() {
        const savedData = localStorage.getItem('emailSignature');
        if (savedData) {
            const data = JSON.parse(savedData);
            this.name = data.name;
            this.title = data.title;
            this.email = data.email;
            this.cell = data.cell;
            this.desk = data.desk;
            this.banner = data.banner;
            this.bannerAssetId = data.bannerAssetId;
            this.bannerTagline = data.bannerTagline;
            this.assetWidth = data.assetWidth;
        }
    },

    /** 
     * Persist signature data to localStorage
     * @this {EmailSignatureState}
     */
    saveToStorage() {
        const dataToSave = {
            name: this.name,
            title: this.title,
            email: this.email,
            cell: this.cell,
            desk: this.desk,
            banner: this.banner,
            bannerAssetId: this.bannerAssetId,
            bannerTagline: this.bannerTagline,
            assetWidth: this.assetWidth
        };
        localStorage.setItem('emailSignature', JSON.stringify(dataToSave));
    },

    /** 
     * Handle banner asset selection and update related fields
     * @this {EmailSignatureState}
     * @param {Event} $event - DOM event from select element
     */
    assetInfo($event) {
        const { target } = $event;
        const values = target.options[target.selectedIndex].dataset;
        this.bannerAssetId = values.assetid;
        this.bannerTagline = values.assettagtext;
        this.assetWidth = values.assetwidth;
        this.updateAndSave();
    },

    /** 
     * Generate preview URL with current signature data
     * @this {EmailSignatureState}
     */
    generateUrl() {
        setTimeout(() => {
            this.adjustImageHeight();
            let baseUrl = window.location.origin + window.location.pathname;
            // Include all non-empty values in URL parameters
            const mapValues = ['name', 'title', 'email', 'cell', 'desk', 'code', 'bannerAssetId', 'bannerTagline', 'ruleHeight'];
            const queryParameters = [];
            mapValues.forEach((value) => {
                if (this[value]) {
                    queryParameters.push(`${value}=${encodeURIComponent(this[value])}`);
                }
            });
            this.url = baseUrl + (queryParameters.length > 0 ? '?' + queryParameters.join('&') : '');
        }, 100);
    },

    /** 
     * Adjust rule height based on banner tagline presence
     * @this {EmailSignatureState}
     * @returns {number} The calculated rule height
     */
    adjustImageHeight() {
        return this.ruleHeight = this.bannerTagline ? 1 : 0;
    }
});
